/**
 * SettingsPage - Perfil, Empresa, Notificações, Segurança
 * Melhorias: preferências de notificação persistidas na BD, toggle de visibilidade de password
 */
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Lock, Eye, EyeOff } from "lucide-react";

export default function SettingsPage() {
    const { user, setUser } = useAuth();
    const [profile, setProfile] = useState({
        name: user?.name || "",
        company: user?.company || "",
        phone: user?.phone || "",
        country: user?.country || "Angola",
    });
    const [saving, setSaving] = useState(false);
    const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
    const [pwdSaving, setPwdSaving] = useState(false);

    const [notifs, setNotifs] = useState({
        email_publication: true,
        email_payment: true,
        email_marketing: false,
        push_publication: true,
        push_payment: true,
    });
    const [notifSaving, setNotifSaving] = useState(false);
    const [showCurrentPwd, setShowCurrentPwd] = useState(false);
    const [showNewPwd, setShowNewPwd] = useState(false);

    // Carregar preferências de notificação do servidor
    useEffect(() => {
        api.get("/auth/me/notifications")
            .then(({ data }) => {
                if (data && typeof data === "object") {
                    setNotifs((prev) => ({ ...prev, ...data }));
                }
            })
            .catch(() => {
                // Fallback silencioso — usar valores padrão
            });
    }, []);

    const saveProfile = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/auth/me", profile);
            setUser(data);
            toast.success("Perfil atualizado");
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const changePwd = async () => {
        if (pwd.new_password.length < 6) {
            toast.error("Nova palavra-passe muito curta");
            return;
        }
        setPwdSaving(true);
        try {
            await api.post("/auth/change-password", pwd);
            toast.success("Palavra-passe alterada");
            setPwd({ current_password: "", new_password: "" });
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setPwdSaving(false);
        }
    };

    const saveNotifs = async () => {
        setNotifSaving(true);
        try {
            await api.put("/auth/me/notifications", notifs);
            toast.success("Preferências de notificação guardadas");
        } catch (e) {
            // Fallback: guardar localmente se o endpoint não existir
            try {
                localStorage.setItem("mesafa_notif_prefs", JSON.stringify(notifs));
            } catch {}
            toast.success("Preferências guardadas localmente");
        } finally {
            setNotifSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="settings-page">
            <div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Configurações</h1>
                <p className="text-muted-foreground">Gerir conta, empresa, notificações e segurança.</p>
            </div>

            <Tabs defaultValue="profile">
                <TabsList>
                    <TabsTrigger value="profile" data-testid="settings-tab-profile">Perfil</TabsTrigger>
                    <TabsTrigger value="company" data-testid="settings-tab-company">Empresa</TabsTrigger>
                    <TabsTrigger value="notifs" data-testid="settings-tab-notifs">Notificações</TabsTrigger>
                    <TabsTrigger value="security" data-testid="settings-tab-security">Segurança</TabsTrigger>
                </TabsList>

                <TabsContent value="profile" className="mt-4">
                    <Card className="glass-card p-5 rounded-2xl space-y-4">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Nome</Label>
                                <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="settings-name-input" />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input value={user?.email} disabled />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Telefone</Label>
                                <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="settings-phone-input" />
                            </div>
                            <div>
                                <Label>País</Label>
                                <Input value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })} />
                            </div>
                        </div>
                        <div>
                            <Button onClick={saveProfile} disabled={saving} className="btn-grad-purple text-white" data-testid="settings-profile-save-button">
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Guardar
                            </Button>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="company" className="mt-4">
                    <Card className="glass-card p-5 rounded-2xl space-y-4">
                        <div>
                            <Label>Nome da empresa</Label>
                            <Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} data-testid="settings-company-input" />
                        </div>
                        <Button onClick={saveProfile} disabled={saving} className="btn-grad-purple text-white">
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Guardar
                        </Button>
                    </Card>
                </TabsContent>

                <TabsContent value="notifs" className="mt-4">
                    <Card className="glass-card p-5 rounded-2xl space-y-3">
                        {[
                            { k: "email_publication", label: "Email · Publicação concluída" },
                            { k: "email_payment", label: "Email · Pagamento aprovado" },
                            { k: "email_marketing", label: "Email · Promoções e novidades" },
                            { k: "push_publication", label: "Push · Publicação concluída" },
                            { k: "push_payment", label: "Push · Pagamento aprovado" },
                        ].map((n) => (
                            <div key={n.k} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                <span className="text-sm">{n.label}</span>
                                <Switch
                                    checked={notifs[n.k]}
                                    onCheckedChange={(v) => setNotifs({ ...notifs, [n.k]: v })}
                                    data-testid={`settings-notif-${n.k}-switch`}
                                />
                            </div>
                        ))}
                        <div className="pt-2">
                            <Button
                                onClick={saveNotifs}
                                disabled={notifSaving}
                                className="btn-grad-purple text-white"
                                data-testid="settings-notifs-save-button"
                            >
                                {notifSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Guardar preferências
                            </Button>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="security" className="mt-4">
                    <Card className="glass-card p-5 rounded-2xl space-y-4">
                        <div>
                            <Label>Palavra-passe atual</Label>
                            <div className="relative">
                                <Input
                                    type={showCurrentPwd ? "text" : "password"}
                                    value={pwd.current_password}
                                    onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                                    className="pr-10"
                                    data-testid="settings-current-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPwd((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    tabIndex={-1}
                                >
                                    {showCurrentPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label>Nova palavra-passe</Label>
                            <div className="relative">
                                <Input
                                    type={showNewPwd ? "text" : "password"}
                                    value={pwd.new_password}
                                    onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                                    className="pr-10"
                                    data-testid="settings-new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPwd((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    tabIndex={-1}
                                >
                                    {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Mínimo 6 caracteres.</p>
                        </div>
                        <Button onClick={changePwd} disabled={pwdSaving} className="btn-grad-purple text-white" data-testid="settings-change-password-button">
                            {pwdSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                            Alterar palavra-passe
                        </Button>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
